import type { AuthSession } from '@aws-amplify/auth';
import { fetchAuthSession } from 'aws-amplify/auth';

export type SessionValidity = 'valid' | 'invalid' | 'unknown';

let inflightRefresh: Promise<AuthSession> | null = null;

export const refreshAuthSession = (): Promise<AuthSession> => {
  if (inflightRefresh) return inflightRefresh;

  inflightRefresh = fetchAuthSession({ forceRefresh: true }).finally(() => {
    inflightRefresh = null;
  });

  return inflightRefresh;
};

/**
 * Heuristically classifies Amplify/auth errors that indicate no active user
 * session, based on common message/name/code fragments.
 */
const isLikelyUnauthenticatedError = (error: unknown): boolean => {
  const message = String((error as any)?.message ?? '').toLowerCase();
  const name = String((error as any)?.name ?? '').toLowerCase();
  const code = String((error as any)?.code ?? '').toLowerCase();

  return (
    message.includes('no current user') ||
    message.includes('not authenticated') ||
    message.includes('no auth tokens') ||
    message.includes('user needs to be authenticated') ||
    name.includes('notauthorized') ||
    code.includes('notauthorized')
  );
};

/**
 * Returns session state as:
 * - `valid`: non-expired ID token is present
 * - `invalid`: token missing/expired or clear unauthenticated auth error
 * - `unknown`: transient/runtime failure where session state is inconclusive
 */
export const getSessionValidity = async (): Promise<SessionValidity> => {
  try {
    const session = await fetchAuthSession();
    const exp = session.tokens?.idToken?.payload?.exp;
    return typeof exp === 'number' && exp * 1000 > Date.now()
      ? 'valid'
      : 'invalid';
  } catch (error) {
    return isLikelyUnauthenticatedError(error) ? 'invalid' : 'unknown';
  }
};

/**
 * Returns true only when Amplify holds a non-expired ID token.
 *
 * This is the reliable way to detect a dead/cleared Cognito session, which
 * often appears as a CORS "Network Error" after a 302 redirect to Hosted UI
 * instead of a clean 401 response.
 *
 * fetchAuthSession() without forceRefresh is usually cheap (cached); this helper
 * returns false for both `invalid` and `unknown` states.
 */
export const isSessionValid = async (): Promise<boolean> => {
  return (await getSessionValidity()) === 'valid';
};
